import { type FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Button,
  CursorPagination,
  Icon,
  PageHeader,
  Select,
  Stat,
  TextInput,
  Textarea,
  cx,
} from '../components/ui/index.js';
import { readJson, useRemote } from '../lib/remote.js';
import { useAdminLocale } from '../lib/admin-locale.js';

interface SiteRow {
  id: string;
  name: string;
  canonicalOrigin: string;
}

interface ContactItem {
  id: string;
  siteId: string;
  siteName: string;
  origin: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  status: string;
  locale: string;
  conversationId: string | null;
  staffNote: string | null;
  createdAt: string;
}

interface ContactPage {
  counts: { new: number; inProgress: number; closed: number };
  items: ContactItem[];
  nextCursor: string | null;
}

const STATUSES = [
  { value: '', el: 'Όλες οι καταστάσεις', en: 'All statuses' },
  { value: 'NEW', el: 'Νέα', en: 'New' },
  { value: 'IN_PROGRESS', el: 'Σε εξέλιξη', en: 'In progress' },
  { value: 'CLOSED', el: 'Ολοκληρώθηκε', en: 'Completed' },
];

const LOCALE_LABEL: Record<string, { el: string; en: string }> = {
  el: { el: 'Ελληνικά', en: 'Greek' },
  en: { el: 'English', en: 'English' },
  fr: { el: 'Français', en: 'French' },
};

function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

function localeLabel(value: string, text: (el: string, en: string) => string): string {
  const label = LOCALE_LABEL[value];
  return label ? text(label.el, label.en) : value;
}

function statusTone(value: string): string {
  if (value === 'CLOSED')
    return 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300';
  if (value === 'IN_PROGRESS') return 'bg-amber-50 dark:bg-amber-950/40 text-amber-800';
  return 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300';
}

function statusDot(value: string): string {
  if (value === 'CLOSED') return 'bg-emerald-50 dark:bg-emerald-950/400';
  if (value === 'IN_PROGRESS') return 'bg-amber-50 dark:bg-amber-950/400';
  return 'bg-blue-600';
}

function StatusPill(props: { value: string }) {
  const { text } = useAdminLocale();
  const status = STATUSES.find((item) => item.value === props.value);
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium',
        statusTone(props.value),
      )}
    >
      <span className={cx('size-1.5 rounded-full', statusDot(props.value))} />
      {status ? text(status.el, status.en) : props.value}
    </span>
  );
}

export function ContactsPage(props: { canWrite: boolean }) {
  const { text, formatDate } = useAdminLocale();
  const [params, setParams] = useSearchParams();
  const [cursor, setCursor] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const siteId = params.get('site') ?? '';
  const status = params.get('status') ?? '';
  const selectedId = params.get('id') ?? '';
  const sites = useRemote<SiteRow[]>('/v1/sites');
  const search = new URLSearchParams();
  if (siteId) search.set('siteId', siteId);
  if (status) search.set('status', status);
  if (query.trim()) search.set('q', query.trim());
  if (cursor) search.set('cursor', cursor);
  const remote = useRemote<ContactPage>(`/v1/leads?${search.toString()}`);
  const items = remote.data?.items ?? [];
  const selected = items.find((item) => item.id === selectedId) ?? null;

  useEffect(() => {
    if (!remote.data) return;
    if (items.some((item) => item.id === selectedId)) return;
    const next = new URLSearchParams(params);
    if (items[0]) next.set('id', items[0].id);
    else next.delete('id');
    if ((next.get('id') ?? '') === selectedId) return;
    setParams(next, { replace: true });
  }, [items, params, remote.data, selectedId, setParams]);

  function setFilter(key: 'site' | 'status', value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('id');
    setCursor(null);
    setParams(next);
  }

  const counts = remote.data?.counts;

  return (
    <section className="space-y-6">
      <PageHeader
        description={text(
          'Αιτήματα που άφησαν οι επισκέπτες από το chatbot των ιστοσελίδων σας.',
          'Requests submitted by visitors through your websites’ chatbot.',
        )}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat
          icon="mail"
          label={text('Νέες επαφές', 'New contacts')}
          value={counts ? String(counts.new) : '—'}
          accent
        />
        <Stat
          icon="pulse"
          label={text('Σε εξέλιξη', 'In progress')}
          value={counts ? String(counts.inProgress) : '—'}
        />
        <Stat
          icon="check"
          label={text('Ολοκληρωμένες', 'Completed')}
          value={counts ? String(counts.closed) : '—'}
        />
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <label className="relative block min-w-0 flex-1">
          <span className="sr-only">{text('Αναζήτηση', 'Search')}</span>
          <Icon
            name="inbox"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400 dark:text-slate-500"
          />
          <TextInput
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setCursor(null);
            }}
            placeholder={text('Αναζήτηση ονόματος ή email', 'Search by name or email')}
            flush
            className="pl-9"
          />
        </label>
        <Select
          aria-label={text('Ιστοσελίδα', 'Website')}
          className="w-full shrink-0 sm:w-56"
          value={siteId}
          onChange={(next) => setFilter('site', next)}
          options={[
            { value: '', label: text('Όλες οι ιστοσελίδες', 'All websites') },
            ...(sites.data ?? []).map((site) => ({
              value: site.id,
              label: hostOf(site.canonicalOrigin),
            })),
          ]}
        />
        <Select
          aria-label={text('Κατάσταση', 'Status')}
          className="w-full shrink-0 sm:w-56"
          value={status}
          onChange={(next) => setFilter('status', next)}
          options={STATUSES.map((item) => ({
            value: item.value,
            label: text(item.el, item.en),
          }))}
        />
      </div>
      {remote.error ? (
        <p className="text-sm text-rose-700 dark:text-rose-300">{remote.error.message}</p>
      ) : null}
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-h-0 flex-col overflow-hidden admin-surface">
          <div className="min-h-0 flex-1 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs tracking-wide text-slate-500 uppercase dark:border-slate-800 dark:text-slate-400">
                  <th className="px-5 py-3 font-medium">{text('Όνομα', 'Name')}</th>
                  <th className="px-5 py-3 font-medium">{text('Ιστοσελίδα', 'Website')}</th>
                  <th className="px-5 py-3 font-medium">{text('Αίτημα', 'Request')}</th>
                  <th className="px-5 py-3 font-medium">{text('Κατάσταση', 'Status')}</th>
                  <th className="px-5 py-3 font-medium">{text('Ημερομηνία', 'Date')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      next.set('id', item.id);
                      setParams(next);
                    }}
                    className={cx(
                      'cursor-pointer',
                      item.id === selected?.id
                        ? 'bg-blue-50 dark:bg-blue-950/40'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                    )}
                  >
                    <td className="px-5 py-3 font-medium text-slate-900 dark:text-slate-100">
                      {item.name}
                    </td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-400">
                      {hostOf(item.origin)}
                    </td>
                    <td className="max-w-48 truncate px-5 py-3 text-slate-600 dark:text-slate-400">
                      {item.message}
                    </td>
                    <td className="px-5 py-3">
                      <StatusPill value={item.status} />
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {formatDate(item.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!remote.data && !remote.error ? (
              <p className="px-5 py-8 text-sm text-slate-500 dark:text-slate-400">
                {text('Φόρτωση…', 'Loading…')}
              </p>
            ) : null}
            {remote.data && items.length === 0 ? (
              <p className="px-5 py-8 text-sm text-slate-500 dark:text-slate-400">
                {text('Δεν υπάρχουν επαφές.', 'No contacts found.')}
              </p>
            ) : null}
          </div>
          <CursorPagination
            hasNext={Boolean(remote.data?.nextCursor)}
            isFirst={!cursor}
            onFirst={() => setCursor(null)}
            onNext={() => setCursor(remote.data?.nextCursor ?? null)}
          />
        </div>
        {selected ? (
          <ContactDetail
            key={selected.id}
            lead={selected}
            canWrite={props.canWrite}
            onSaved={() => void remote.mutate()}
          />
        ) : (
          <aside className="admin-surface p-6 text-sm text-slate-500 dark:text-slate-400">
            {text(
              'Επιλέξτε μια επαφή για να δείτε το αίτημα και τη συνομιλία.',
              'Select a contact to view the request and conversation.',
            )}
          </aside>
        )}
      </div>
    </section>
  );
}

function ContactDetail(props: { lead: ContactItem; canWrite: boolean; onSaved: () => void }) {
  const { text } = useAdminLocale();
  const lead = props.lead;
  const [status, setStatus] = useState(lead.status);
  const [note, setNote] = useState(lead.staffNote ?? '');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setNotice(null);
    setError(null);
    try {
      await readJson(`/v1/sites/${lead.siteId}/leads/${lead.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, staffNote: note.trim() || null }),
      });
      setNotice(text('Αποθηκεύτηκε.', 'Saved.'));
      props.onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : text('Η αποθήκευση απέτυχε', 'Save failed'),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <aside className="admin-surface">
      <header className="border-b border-slate-100 dark:border-slate-800 px-6 py-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {text('Στοιχεία επαφής', 'Contact details')}
        </h3>
        <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
          {text('Στοιχεία που υπέβαλε ο επισκέπτης.', 'Details submitted by the visitor.')}
        </p>
      </header>
      <div className="space-y-5 p-6">
        <dl className="space-y-3 text-sm">
          <Detail label={text('Όνομα', 'Name')} value={lead.name} />
          <Detail label="Email" value={lead.email} />
          <Detail label={text('Τηλέφωνο', 'Phone')} value={lead.phone ?? '—'} />
          <Detail label={text('Ιστοσελίδα', 'Website')} value={hostOf(lead.origin)} />
          <Detail label={text('Γλώσσα', 'Language')} value={localeLabel(lead.locale, text)} />
        </dl>
        <div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {text('Μήνυμα', 'Message')}
          </p>
          <p className="mt-2 rounded-lg bg-slate-50 dark:bg-slate-950 px-3 py-2 text-sm whitespace-pre-wrap text-slate-800 dark:text-slate-200">
            {lead.message || '—'}
          </p>
        </div>
        <form onSubmit={(event) => void save(event)} className="space-y-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {text('Κατάσταση', 'Status')}
            <Select
              className="mt-1.5"
              value={status}
              disabled={!props.canWrite}
              onChange={setStatus}
              options={STATUSES.filter((item) => item.value).map((item) => ({
                value: item.value,
                label: text(item.el, item.en),
              }))}
            />
          </label>
          {lead.conversationId ? (
            <Link
              to={`/conversations?site=${lead.siteId}&id=${lead.conversationId}`}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Icon name="chat" className="size-4" />
              {text('Προβολή συνομιλίας', 'View conversation')}
            </Link>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {text('Δεν συνδέεται με συνομιλία.', 'Not linked to a conversation.')}
            </p>
          )}
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {text('Εσωτερική σημείωση', 'Internal note')}
            <Textarea
              value={note}
              disabled={!props.canWrite}
              onChange={(event) => setNote(event.target.value)}
              maxLength={2000}
              rows={4}
              placeholder={text(
                'Σημείωση ορατή μόνο στην ομάδα σας.',
                'Note visible only to your team.',
              )}
            />
          </label>
          {props.canWrite ? (
            <Button type="submit" disabled={pending} className="w-full">
              <Icon name="save" className="size-4" />
              {pending ? text('Αποθήκευση…', 'Saving…') : text('Αποθήκευση', 'Save')}
            </Button>
          ) : null}
          {notice ? (
            <p className="text-sm text-emerald-700 dark:text-emerald-300">{notice}</p>
          ) : null}
          {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
        </form>
      </div>
    </aside>
  );
}

function Detail(props: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-slate-500 dark:text-slate-400 dark:text-slate-500">{props.label}</dt>
      <dd className="text-right font-medium break-all text-slate-900 dark:text-slate-100">
        {props.value}
      </dd>
    </div>
  );
}
